export type TreeNavigator = {
  up: (count?: number) => void;
  down: (count?: number) => void;
  home: () => void;
  end: () => void;
  scrollToIndex: (index: number) => void;
  scrollToFocusedNode: () => void;
};

export function useTreeNavigation(
  sizesProp: {
    treeHeight: number;
    nodeHeight: number;
    nodeCount: number;
  },
  currentPageProps: {
    startNodeIndex: number;
    renderedNodeCount: number;
    scrollTop: number;
    setScrollTop: (value: number) => void;
  },
  focusProps: {
    getCurrentFocusIndex: () => number;
    setCurrentFocusIndex: (value: number) => void;
    focusTreeNode: (rowIndex: number) => void;
  }
): TreeNavigator {
  const { treeHeight, nodeHeight, nodeCount } = sizesProp;
  const { startNodeIndex, renderedNodeCount, scrollTop, setScrollTop } = currentPageProps;
  const { getCurrentFocusIndex, setCurrentFocusIndex, focusTreeNode } = focusProps;

  const maxScrollTop = Math.max(0, Math.round(nodeCount * nodeHeight - treeHeight));

  // The variables with suffix nodeIndex suggests the index in the flattened data model
  // where as those with suffix rowIndex suggest the renderer index,
  const firstVisibleRowIndex =
    Math.round(Math.min(scrollTop, maxScrollTop) / nodeHeight) - startNodeIndex;
  const lastVisibleRowIndex =
    firstVisibleRowIndex + Math.min(nodeCount - 1, Math.round(treeHeight / nodeHeight) - 1);
  const visibleRowCount = lastVisibleRowIndex - firstVisibleRowIndex + 1;

  const firstVisibleRowNodeIndex = startNodeIndex + firstVisibleRowIndex;
  const endVisibleRowNodeIndex = firstVisibleRowNodeIndex + visibleRowCount - 1;

  return {
    up: function (count = 1) {
      if (getCurrentFocusIndex() === 0) {
        return;
      }
      const currentRow = getCurrentFocusIndex() - startNodeIndex;
      setCurrentFocusIndex(getCurrentFocusIndex() - count);
      if (firstVisibleRowIndex === 0 || currentRow > firstVisibleRowIndex + count) {
        focusTreeNode(currentRow - count);
      } else {
        this.scrollToIndex(getCurrentFocusIndex() - 1);
      }
    },
    down: function (count = 1) {
      if (getCurrentFocusIndex() === nodeCount - 1) {
        return;
      }
      const currentRow = getCurrentFocusIndex() - startNodeIndex;
      setCurrentFocusIndex(getCurrentFocusIndex() + count);
      if (
        lastVisibleRowIndex === renderedNodeCount - 1 ||
        currentRow < lastVisibleRowIndex - count
      ) {
        focusTreeNode(currentRow + count);
      } else {
        this.scrollToIndex(
          getCurrentFocusIndex() - (lastVisibleRowIndex - firstVisibleRowIndex - 1)
        );
      }
    },
    home: function () {
      setCurrentFocusIndex(0);
      if (firstVisibleRowNodeIndex === 0) {
        focusTreeNode(0);
      } else {
        this.scrollToIndex(0);
      }
    },
    end: function () {
      setCurrentFocusIndex(nodeCount - 1);
      if (endVisibleRowNodeIndex === nodeCount - 1) {
        focusTreeNode(renderedNodeCount - 1);
      } else {
        this.scrollToIndex(nodeCount - 1);
      }
    },

    scrollToIndex: function (index: number) {
      setScrollTop(Math.min(maxScrollTop, index * nodeHeight));
    },

    scrollToFocusedNode: function () {
      const currentFocusedRowIndex =
        firstVisibleRowNodeIndex <= getCurrentFocusIndex() &&
        endVisibleRowNodeIndex >= getCurrentFocusIndex()
          ? getCurrentFocusIndex() - startNodeIndex
          : -1;

      if (currentFocusedRowIndex === -1) {
        this.scrollToIndex(Math.max(0, getCurrentFocusIndex() - (visibleRowCount - 2)));
      } else {
        focusTreeNode(currentFocusedRowIndex);
      }
    },
  };
}
